import { useEffect, useState } from "react";
import { useAppContext } from "../context/app/context";
import { useLocation, useNavigate } from "react-router-dom";
import { useValidateTokenLazyQuery } from "../graphql/generated/hooks";
import { setUser } from "../context/app/action";
import { LOCAL_STORAGE_KEYS } from "../common/constants";

const passingPaths = [
  "/login",
  "/invite",
  "/forgot-password",
  "/reset-password",
];

export function useAuthenticate() {
  const location = useLocation();
  const navigate = useNavigate();
  // eslint-disable-next-line
  const [validateToken] = useValidateTokenLazyQuery();
  const appContext = useAppContext();
  
  // Check if current path is a public route - do this synchronously before useState
  const currentPath = location.pathname;
  const isInviteRoute = currentPath.startsWith("/invite/");
  const isLoginRoute = currentPath.startsWith("/login");
  const isForgotPasswordRoute = currentPath.startsWith("/forgot-password");
  const isResetPasswordRoute = currentPath.startsWith("/reset-password/");
  const isPublicRouteSync = isInviteRoute || isLoginRoute || isForgotPasswordRoute || isResetPasswordRoute;

  // Initialize state based on whether it's a public route
  const [loading, setLoading] = useState(!isPublicRouteSync);
  const [authenticated, setAuthenticated] = useState(isPublicRouteSync);

  useEffect(() => {
    // Recalculate on every pathname change to ensure we have the latest value
    const currentPathCheck = location.pathname;
    const isInviteRouteCheck = currentPathCheck.startsWith("/invite/");
    const isLoginRouteCheck = currentPathCheck.startsWith("/login");
    const isForgotPasswordRouteCheck = currentPathCheck.startsWith("/forgot-password");
    const isResetPasswordRouteCheck = currentPathCheck.startsWith("/reset-password/");
    const isPublicRouteCheck = isInviteRouteCheck || isLoginRouteCheck || isForgotPasswordRouteCheck || isResetPasswordRouteCheck;
    
    // Early exit for public routes - don't even start the async function
    if (isPublicRouteCheck) {
      setLoading(false);
      setAuthenticated(true);
      return;
    }
    
    let isMounted = true;
    
    (async () => {
      try {
        // Recalculate public route check inside useEffect to ensure we have the latest pathname
        const currentPathInEffect = location.pathname;
        const isInviteRouteInEffect = currentPathInEffect.startsWith("/invite/");
        const isLoginRouteInEffect = currentPathInEffect.startsWith("/login");
        const isForgotPasswordRouteInEffect = currentPathInEffect.startsWith("/forgot-password");
        const isResetPasswordRouteInEffect = currentPathInEffect.startsWith("/reset-password/");
        const isPublicRouteInEffect = isInviteRouteInEffect || isLoginRouteInEffect || isForgotPasswordRouteInEffect || isResetPasswordRouteInEffect;
        
        // Allow access to public paths without authentication
        // Early return for public routes - this MUST happen before any token checks
        if (isPublicRouteInEffect) {
          if (isMounted) {
            setLoading(false);
            setAuthenticated(true);
          }
          return;
        }
        
        // Check for exact match or paths that start with the passing path (for routes with params like /invite/:token)
        // Also handle trailing slashes
        const normalizedPathname = currentPathInEffect.replace(/\/$/, '');
        
        // Check against passing paths for other public routes
        const isPublicPath = passingPaths.some((path) => {
          // Check exact match
          if (normalizedPathname === path || currentPathInEffect === path) {
            return true;
          }
          // Check if path starts with the public path followed by a slash (e.g., /invite/xxx)
          if (currentPathInEffect.startsWith(path + '/') || normalizedPathname.startsWith(path + '/')) {
            return true;
          }
          return false;
        });
        
        if (isPublicPath) {
          if (isMounted) {
            setLoading(false);
            setAuthenticated(true);
          }
          return;
        }

        // Read tokens fresh from localStorage inside useEffect to get latest values
        const sessionToken = localStorage.getItem(LOCAL_STORAGE_KEYS.SESSION_TOKEN);
        const refreshToken = localStorage.getItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN);

        // Redirect to login if no tokens are present
        // BUT don't redirect if we're on a public route (already handled above)
        if (!sessionToken || !refreshToken) {
          // Double-check we're not on a public route (safety check)
          if (isMounted && !isPublicRouteInEffect && currentPathInEffect !== "/login") {
            navigate(`/login`);
          }
          if (isMounted) {
            setLoading(false);
          }
          return;
        }

        // Validate the current token
        // If token is expired, authLink will automatically refresh it and retry
        validateToken()
          .then((res) => {
            if (!res.data?.validateToken) {
              if (isMounted) {
                setLoading(false);
              }
              return;
            }

            const user = JSON.parse(res.data.validateToken);
            
            // Verify user has partner role and partnerId
            if (!user) {
              localStorage.removeItem(LOCAL_STORAGE_KEYS.SESSION_TOKEN);
              localStorage.removeItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN);
              if (isMounted) {
                navigate(`/login`);
                setLoading(false);
              }
              return;
            }

            if (!user.roles?.includes("partner")) {
              alert("Access Denied: This portal is for partners only.");
              localStorage.removeItem(LOCAL_STORAGE_KEYS.SESSION_TOKEN);
              localStorage.removeItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN);
              if (isMounted) {
                navigate(`/login`);
                setLoading(false);
              }
              return;
            }

            if (!user.partnerId) {
              alert("Access Denied: Your account is not associated with a partner.");
              localStorage.removeItem(LOCAL_STORAGE_KEYS.SESSION_TOKEN);
              localStorage.removeItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN);
              if (isMounted) {
                navigate(`/login`);
                setLoading(false);
              }
              return;
            }

            // Extract permissionNames and permissions from user object
            // If permissionNames is empty but permissions array exists, extract names from permissions
            let permissionNames = user.permissionNames || [];
            let permissions = user.permissions || [];
            
            // Check if permissions is in a different format (e.g., nested in user object)
            if (!permissions || permissions.length === 0) {
              permissions = user.user?.permissions || user.data?.permissions || [];
            }
            
            // If permissionNames is empty but we have permissions array, extract names
            if (permissionNames.length === 0 && permissions.length > 0) {
              permissionNames = permissions.map((perm: any) => {
                if (typeof perm === 'string') return perm;
                return perm.name || perm.permissionName || perm;
              }).filter(Boolean);
            }
            
            const storeId = user.storeId || user.store_id || user.user?.storeId;

            // Check if user is store admin (has "store admin" permission but not "Super Partner")
            // Try multiple variations of the permission name for robustness
            const hasStoreAdminPermission = permissionNames.some((name: string) => 
              name?.toLowerCase().includes("store admin") || 
              name?.toLowerCase() === "store admin" ||
              name === "store admin"
            );
            const hasSuperPartner = permissionNames.some((name: string) => 
              name?.toLowerCase().includes("super partner") || 
              name?.toLowerCase() === "super partner" ||
              name === "Super Partner"
            );
            const isStoreAdmin = hasStoreAdminPermission && !hasSuperPartner;
            const isSuperPartner = hasSuperPartner;

            // Store user data in app context
            if (isMounted) {
              // Permission-based routing - check BEFORE setting authenticated
              const currentPath = window.location.pathname;
              const normalizedCurrentPath = currentPath.replace(/\/$/, '') || '/';
              // Check if path is root or dashboard
              const isRootOrDashboard = normalizedCurrentPath === '/' || normalizedCurrentPath === '/dashboard';
              const isOperationPanelPath = normalizedCurrentPath.startsWith("/operation-panel");
              const isPublicPath = passingPaths.some((path) => {
                const normalizedPath = currentPath.replace(/\/$/, '');
                return normalizedPath === path || normalizedPath.startsWith(path + '/');
              });
              
              // Don't redirect store admin if they're on a public route (like invite)
              // Public routes should be accessible to everyone, even when logged in
              if (isStoreAdmin && !isPublicPath) {
                // Always redirect store admin to operation panel if not already there
                if (!isOperationPanelPath || isRootOrDashboard) {
                  // Redirect to operation panel with storeId if available
                  // This handles root path "/", "/dashboard", and any other non-operation-panel paths
                  const redirectPath = storeId ? `/operation-panel/${storeId}` : "/operation-panel";
                  
                  // Set user data first so ProtectedRoute can use it
                  appContext.dispatch(
                    setUser({
                      id: user.user_id || user.id,
                      username: user.username,
                      partnerId: user.partnerId,
                      permissionNames: permissionNames,
                      permissions: permissions,
                      storeId: storeId,
                    })
                  );
                  
                  // Navigate immediately
                  navigate(redirectPath, { replace: true });
                  setAuthenticated(true);
                  setLoading(false);
                  return;
                } else {
                  // If already on operation panel, check if storeId matches
                  const pathMatch = normalizedCurrentPath.match(/^\/operation-panel\/(\d+)/);
                  if (pathMatch && storeId) {
                    const urlStoreId = parseInt(pathMatch[1], 10);
                    if (urlStoreId !== storeId) {
                      // Redirect to correct storeId
                      navigate(`/operation-panel/${storeId}`, { replace: true });
                      appContext.dispatch(
                        setUser({
                          id: user.user_id || user.id,
                          username: user.username,
                          partnerId: user.partnerId,
                          permissionNames: permissionNames,
                          permissions: permissions,
                          storeId: storeId,
                        })
                      );
                      setAuthenticated(true);
                      setLoading(false);
                      return;
                    }
                  } else if (storeId && !pathMatch) {
                    // On /operation-panel without ID, redirect to specific store
                    navigate(`/operation-panel/${storeId}`, { replace: true });
                    appContext.dispatch(
                      setUser({
                        id: user.user_id || user.id,
                        username: user.username,
                        partnerId: user.partnerId,
                        permissionNames: permissionNames,
                        permissions: permissions,
                        storeId: storeId,
                      })
                    );
                    setAuthenticated(true);
                    setLoading(false);
                    return;
                  }
                }
              }
              
              // For non-store-admin users, set user data normally
              appContext.dispatch(
                setUser({
                  id: user.user_id || user.id,
                  username: user.username,
                  partnerId: user.partnerId,
                  permissionNames: permissionNames,
                  permissions: permissions,
                  storeId: storeId,
                })
              );
              setAuthenticated(true);
              setLoading(false);
              
              // If user is on /403 page after successful auth, redirect appropriately
              if (currentPath === "/403") {
                const intendedPath = localStorage.getItem(LOCAL_STORAGE_KEYS.INTENDED_PATH);
                localStorage.removeItem(LOCAL_STORAGE_KEYS.INTENDED_PATH);
                
                // Redirect store admin to operation panel with storeId, others to dashboard
                let redirectPath: string;
                if (isStoreAdmin) {
                  redirectPath = storeId ? `/operation-panel/${storeId}` : "/operation-panel";
                } else {
                  redirectPath = intendedPath || "/dashboard";
                }
                
                // Set loop protection before redirecting to detect if this path is forbidden again
                if (redirectPath !== "/dashboard" && !redirectPath.startsWith("/operation-panel")) {
                  sessionStorage.setItem("last_403_path", redirectPath);
                  sessionStorage.setItem("last_403_time", Date.now().toString());
                } else {
                  // Going to dashboard or operation panel - clear protection
                  sessionStorage.removeItem("last_403_path");
                  sessionStorage.removeItem("last_403_time");
                }
                
                navigate(redirectPath);
              } else {
                // Not on /403, clear protection
                sessionStorage.removeItem("last_403_path");
                sessionStorage.removeItem("last_403_time");
              }
            }
          })
          .catch((error) => {
            // Only logout if it's a critical auth error, not a network issue
            const isAuthFailure = 
              error?.graphQLErrors?.some((err: any) => 
                err.extensions?.code === "UNAUTHENTICATED" ||
                err.extensions?.code === "UNAUTHORIZED_TOKEN_VALIDATION"
              );

            if (isAuthFailure) {
              localStorage.removeItem(LOCAL_STORAGE_KEYS.SESSION_TOKEN);
              localStorage.removeItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN);
              if (isMounted) {
                navigate(`/login`);
              }
            }
            
            if (isMounted) {
              setLoading(false);
            }
          });
      } catch (e) {
        // Clear both tokens on unexpected errors
        localStorage.removeItem(LOCAL_STORAGE_KEYS.SESSION_TOKEN);
        localStorage.removeItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN);
        if (isMounted) {
          setLoading(false);
          navigate(`/login`);
        }
      }
    })();
    
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // validateToken and appContext are stable references from hooks, but we need location.pathname to re-run on route changes
    // Include isPublicRouteSync to re-run when route changes from public to private or vice versa
  }, [location.pathname, isPublicRouteSync]);

  return {
    loading,
    authenticated,
  };
}
